/**
 * SlotDetailsModal — деталка активных контрактов одного типа слота.
 *
 * Открывается по тапу на SlotCard с hasOwned > 0. Внутри:
 *   - Hero с иконкой, тарифом, счётчиком ×N
 *   - Суммарные показатели: staked, earned, средний прогресс
 *   - Список каждого экземпляра контракта:
 *       · contractId (сокращённо)
 *       · earned / cap с прогресс-баром
 *       · daysActive, next claim, Plasma Core статус
 *   - CTA "Открыть ещё 1" (deposit modal)
 *
 * Пока данные — моковые (из SlotCard props). Реальные из API
 * подтянутся в задаче 11 (Dashboard integration).
 */
import { useEffect, useMemo, type CSSProperties } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Flame, Plus, X, Zap, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import type { VentureSlotInfo } from '@/data/ventureSlots';
import type { ActiveSlotInstance } from './SlotCard';

interface SlotDetailsModalProps {
  slot: VentureSlotInfo | null;
  instances: ActiveSlotInstance[];
  onClose: () => void;
  onBuyMore: () => void;
}

function formatUSDT(v: number, locale: string): string {
  return v.toLocaleString(locale, { maximumFractionDigits: 2 });
}

function shortId(id: string): string {
  if (id.length <= 10) return id;
  return `${id.slice(0, 6)}…${id.slice(-4)}`;
}

export function SlotDetailsModal({
  slot,
  instances,
  onClose,
  onBuyMore,
}: SlotDetailsModalProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const open = slot !== null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const totals = useMemo(() => {
    if (!slot)
      return {
        count: 0,
        stakedTotal: 0,
        earnedTotal: 0,
        capTotal: 0,
        progressPct: 0,
        anyPc: false,
      };
    const count = instances.length;
    const stakedTotal = slot.amount * count;
    const earnedTotal = instances.reduce((s, i) => s + i.earned, 0);
    const capTotal = stakedTotal * 2.5;
    const progressPct =
      capTotal > 0 ? Math.min(100, (earnedTotal / capTotal) * 100) : 0;
    const anyPc = instances.some((i) => i.plasmaCoreActive);
    return { count, stakedTotal, earnedTotal, capTotal, progressPct, anyPc };
  }, [slot, instances]);

  if (!slot) return null;

  const hue = slot.hue;
  const accent = `hsl(${hue} 80% 65%)`;
  const accentSoft = `hsl(${hue} 70% 55% / 0.18)`;
  const accentGlow = `hsl(${hue} 80% 60% / 0.45)`;

  const modalStyle: CSSProperties = {
    background:
      'linear-gradient(180deg, var(--glass-fill-strong) 0%, var(--glass-fill) 100%)',
    border: '1px solid var(--glass-stroke-strong)',
    boxShadow:
      'var(--glass-shadow), inset 0 1.5px 0 0 var(--glass-rim-light), inset 0 -1px 0 0 var(--glass-rim-dark)',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[55] flex items-center justify-center px-3 sm:px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.24 }}
          onClick={() => { uiSounds.bellClose(); onClose(); }}
        >
          <div className="absolute inset-0 bg-black/65 backdrop-blur-sm [.platform-non-ios_&]:bg-black/90 [.platform-non-ios_&]:backdrop-blur-none" />

          {/* Two-layer wrapper: outer = fixed shell with close button,
              inner = scrollable content. This avoids the Safari bug where
              `position: absolute` inside `overflow-y: auto` misplaces
              the close button to the left edge. */}
          <motion.div
            className="relative w-full max-w-md rounded-[28px]"
            style={{ ...modalStyle, maxHeight: 'calc(100dvh - 48px)', overflow: 'hidden auto' }}
            initial={{ y: 60, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button — flow-layout, flex justify-end.
                NOT absolute — Telegram WebView on iOS breaks
                absolute+right inside overflow containers. */}
            <div className="flex justify-end px-3 pt-3 pb-0">
              <button
                type="button"
                aria-label={t('slotDetails.closeAria')}
                onClick={() => {
                  haptic.light();
                  onClose();
                }}
                className="h-8 w-8 rounded-full lg-pill flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] active:scale-90 transition"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>

            {/* ── Hero ── */}
            <div className="relative flex items-start gap-4 px-5 pt-0 pb-4">
              <div
                className="flex-shrink-0 flex items-center justify-center rounded-[26px] relative"
                style={{
                  width: 100,
                  height: 100,
                  background: accentSoft,
                  border: `1.5px solid ${accent}`,
                  boxShadow: `0 0 56px -6px ${accentGlow}, inset 0 1px 0 0 rgba(255,255,255,0.25)`,
                }}
              >
                <img
                  src={slot.icon}
                  alt={slot.name}
                  width={70}
                  height={70}
                  style={{
                    filter: `drop-shadow(0 0 14px ${accentGlow})`,
                  }}
                />
                {totals.count > 1 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[24px] h-[24px] rounded-full flex items-center justify-center font-mono font-extrabold text-[11px] text-white px-1"
                    style={{
                      background: `linear-gradient(180deg, ${accent}, color-mix(in oklab, ${accent} 60%, #000))`,
                      border: '1.5px solid var(--bg-base)',
                      boxShadow: `0 4px 12px -2px ${accentGlow}`,
                    }}
                  >
                    ×{totals.count}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0 pr-14">
                <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono">
                  {t('slotDetails.contractLine', { month: slot.unlockMonth })}
                </p>
                <h2 className="font-display text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight leading-tight mt-0.5">
                  {slot.name}
                </h2>
                <p className="text-[11px] text-[var(--text-secondary)] leading-snug mt-0.5">
                  {slot.tagline}
                </p>
              </div>
            </div>

            {/* ── Aggregate stats ── */}
            <div className="px-5 pb-3">
              <div
                className="rounded-2xl p-3.5"
                style={{
                  background: 'var(--glass-fill)',
                  border: '1px solid var(--glass-stroke)',
                }}
              >
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-mono">
                      {t('slotDetails.stake')}
                    </p>
                    <p className="font-display text-[19px] font-extrabold text-[var(--text-primary)] tabular-nums mt-0.5">
                      {formatUSDT(totals.stakedTotal, locale)}{' '}
                      <span className="text-[11px] text-[var(--text-muted)] font-bold">
                        USDT
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-mono">
                      {t('slotDetails.earned')}
                    </p>
                    <p
                      className="font-mono text-[15px] font-extrabold tabular-nums mt-0.5"
                      style={{ color: accent }}
                    >
                      +{formatUSDT(totals.earnedTotal, locale)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-[9px] font-mono uppercase tracking-[0.14em] text-[var(--text-muted)] mb-1.5 mt-2">
                  <span>{t('slotDetails.progress25')}</span>
                  <span
                    className="font-extrabold"
                    style={{ color: accent }}
                  >
                    {totals.progressPct.toFixed(1)}%
                  </span>
                </div>
                <div
                  className="h-[5px] rounded-full overflow-hidden"
                  style={{ background: 'var(--glass-stroke)' }}
                >
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${totals.progressPct}%`,
                      background: `linear-gradient(90deg, ${accent}, color-mix(in oklab, ${accent} 60%, var(--accent-secondary)))`,
                      boxShadow: `0 0 10px ${accentGlow}`,
                      transition: 'width 0.6s cubic-bezier(0.32, 0.72, 0, 1)',
                    }}
                  />
                </div>
              </div>
            </div>

            {/* ── Per-instance list ── */}
            <div className="px-5 pb-3">
              <p className="text-[9px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono mb-2">
                {t('slotDetails.yourContracts')}
              </p>
              <ul
                className="space-y-2 overflow-y-auto pr-0.5"
                style={{ maxHeight: 'min(40vh, 260px)' }}
              >
                {instances.map((inst, idx) => {
                  const cap = slot.amount * 2.5;
                  const pct = Math.min(100, (inst.earned / cap) * 100);
                  return (
                    <li
                      key={inst.contractId}
                      className="rounded-2xl p-3"
                      style={{
                        background: 'var(--glass-fill)',
                        border: '1px solid var(--glass-stroke)',
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="inline-flex items-center justify-center h-6 w-6 rounded-full font-mono font-extrabold text-[10px]"
                            style={{
                              background: accentSoft,
                              color: accent,
                              border: `1px solid ${accent}`,
                            }}
                          >
                            {idx + 1}
                          </span>
                          <div className="min-w-0">
                            <p className="text-[11px] font-mono text-[var(--text-secondary)] truncate">
                              {shortId(inst.contractId)}
                            </p>
                            <p className="text-[9px] text-[var(--text-muted)] font-mono uppercase tracking-[0.12em]">
                              {i18n.language === 'ru' ? `${inst.daysActive} дн` : `${inst.daysActive}d`} · {formatUSDT(inst.earned, locale)}{' '}
                              / {formatUSDT(cap, locale)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {inst.plasmaCoreActive && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-[2px] rounded-full text-[9px] font-mono font-extrabold"
                              style={{
                                background:
                                  'color-mix(in oklab, var(--accent-secondary) 18%, transparent)',
                                color: 'var(--accent-secondary)',
                                border:
                                  '1px solid color-mix(in oklab, var(--accent-secondary) 40%, transparent)',
                              }}
                            >
                              <Zap size={9} strokeWidth={2.5} />
                              Plasma Core
                            </span>
                          )}
                          <span
                            className="font-mono text-[11px] font-extrabold tabular-nums"
                            style={{ color: accent }}
                          >
                            {pct.toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div
                        className="h-[4px] rounded-full overflow-hidden mt-2"
                        style={{ background: 'var(--glass-stroke)' }}
                      >
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${pct}%`,
                            background: `linear-gradient(90deg, ${accent}, color-mix(in oklab, ${accent} 60%, var(--accent-secondary)))`,
                            boxShadow: `0 0 8px ${accentGlow}`,
                            transition: 'width 0.5s ease-out',
                          }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* ── Footer info row ── */}
            <div className="px-5 pb-3 grid grid-cols-3 gap-2">
              <div
                className="rounded-xl px-2.5 py-2 text-center"
                style={{
                  background: 'var(--glass-fill)',
                  border: '1px solid var(--glass-stroke)',
                }}
              >
                <p className="text-[8px] font-mono uppercase tracking-[0.14em] text-[var(--text-muted)]">
                  Rate/мес
                </p>
                <p
                  className="font-mono text-[12px] font-extrabold tabular-nums mt-0.5"
                  style={{ color: accent }}
                >
                  0,01–{slot.monthlyRate}%
                </p>
              </div>
              <div
                className="rounded-xl px-2.5 py-2 text-center"
                style={{
                  background: 'var(--glass-fill)',
                  border: '1px solid var(--glass-stroke)',
                }}
              >
                <p className="text-[8px] font-mono uppercase tracking-[0.14em] text-[var(--text-muted)]">
                  Cycle
                </p>
                <p className="font-mono text-[12px] font-extrabold text-[var(--text-primary)] tabular-nums mt-0.5 flex items-center justify-center gap-0.5">
                  <Clock size={10} strokeWidth={2.6} />
                  {totals.anyPc ? '7 дн' : '24 ч'}
                </p>
              </div>
              <div
                className="rounded-xl px-2.5 py-2 text-center"
                style={{
                  background: 'var(--glass-fill)',
                  border: '1px solid var(--glass-stroke)',
                }}
              >
                <p className="text-[8px] font-mono uppercase tracking-[0.14em] text-[var(--text-muted)]">
                  Plasma Core
                </p>
                <p
                  className="font-mono text-[12px] font-extrabold tabular-nums mt-0.5"
                  style={{
                    color: totals.anyPc
                      ? 'var(--accent-secondary)'
                      : 'var(--text-muted)',
                  }}
                >
                  {totals.anyPc ? 'ON' : 'off'}
                </p>
              </div>
            </div>

            {/* ── CTA ── */}
            <div className="px-5 pb-6 pt-1">
              <button
                type="button"
                onClick={() => {
                  haptic.medium();
                  onBuyMore();
                }}
                className="w-full rounded-2xl py-3 flex items-center justify-center gap-2 font-display font-extrabold text-[14px] transition-all active:scale-[0.98]"
                style={{
                  background: `linear-gradient(180deg, ${accent}, color-mix(in oklab, ${accent} 70%, #000))`,
                  color: '#0a0a14',
                  boxShadow: `0 18px 40px -12px ${accentGlow}, inset 0 1.5px 0 0 rgba(255,255,255,0.4)`,
                }}
              >
                <Plus size={16} strokeWidth={2.6} />
                {t('slotDetails.openMore')}
              </button>
              <p className="text-[10px] text-center text-[var(--text-muted)] mt-2 font-mono">
                <Flame size={9} className="inline mr-1" strokeWidth={2.4} />
                новый независимый контракт · свой 250% лимит
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
